import { useState, useEffect } from 'react';
import { aquaApi } from '../aquaApi';
import { fmtDate } from '../dateUtils';
import DateInput from '../DateInput';

const FEED_TYPES = ['CP-1', 'CP-2', 'CP-3', 'CP-4', 'CP-5', 'OTHER'];

const today = new Date().toISOString().split('T')[0];

const EMPTY_HDR = { cropId: '', feedDate: today };

const FEED_NOS = ['F1', 'F2', 'F3', 'F4'];

const EMPTY_ROW = {
  pondNo: '', feedName: '', feedType: 'CP-1',
  feedNo: 'F1', amount: '0', doc: '',
  bf1: '', bf2: '',
  bags: '0', bagWeight: '25', looseKg: '0', cumFeed: '0',
  feedTime: '', feedCost: '0',
};

function makeRow() { return { ...EMPTY_ROW }; }

function calcDocFromRelease(pondNo, feedDate, releaseMap) {
  const rd = releaseMap[pondNo];
  if (!rd || !feedDate) return '';
  const diff = Math.floor((new Date(feedDate) - new Date(rd)) / 86400000);
  return diff > 0 ? String(diff) : '';
}

const fmt3 = (v) => parseFloat(v || 0).toFixed(3);
const dayTotal = (row) => parseFloat(row.amount) || 0;
const f3d = (v) => { const n = parseFloat(v || 0); return n > 0 ? n.toFixed(3) : '—'; };

function computeBagsLoose(dayFeed, bagWeight) {
  const dF = parseFloat(dayFeed)  || 0;
  const bW = parseFloat(bagWeight) || 0;
  if (bW <= 0 || dF <= 0) return { bags: 0, loose: dF };
  const bags  = Math.floor(dF / bW);
  const loose = parseFloat((dF - bags * bW).toFixed(3));
  return { bags, loose };
}

const BLIND_CHART = [
  [1,1.5],[2,2.0],[3,2.5],[4,3.0],[5,3.5],[6,4.0],[7,5.0],[8,6.0],[9,7.0],[10,8.0],
  [11,9.0],[12,10.0],[13,11.0],[14,12.0],[15,13.0],[16,14.0],[17,15.0],[18,16.0],[19,17.0],[20,18.0],
  [21,20.0],[22,22.0],[23,24.0],[24,26.0],[25,28.0],[26,30.0],[27,32.0],[28,34.0],[29,36.0],[30,38.0],
];
function blindChartValue(doc) {
  const d = Math.min(30, Math.max(1, Math.round(doc)));
  return BLIND_CHART[d - 1][1];
}
function suggestedFeedRate(abw) {
  const w = parseFloat(abw) || 0;
  if (w <= 0)  return '';
  if (w <= 1)  return '20';
  if (w <= 3)  return '11.5';
  if (w <= 8)  return '6';
  if (w <= 15) return '3.25';
  if (w <= 25) return '2.5';
  return '2';
}
const FEED_DIST = [
  { time: '06:00 AM', pct: 20 }, { time: '10:00 AM', pct: 25 },
  { time: '02:00 PM', pct: 25 }, { time: '06:00 PM', pct: 20 },
  { time: '10:00 PM', pct: 10 },
];
const ABW_RATES = [
  { range: '0.1 – 1 g',  rate: '15 – 25%' }, { range: '1 – 3 g',   rate: '8 – 15%'  },
  { range: '3 – 8 g',    rate: '4 – 8%'   }, { range: '8 – 15 g',  rate: '2.5 – 4%' },
  { range: '15 – 25 g',  rate: '2 – 3%'   }, { range: '25+ g',     rate: '1.5 – 2.5%' },
];

const DAY_NAMES = ['SUN','MON','TUE','WED','THU','FRI','SAT'];
function fmtGroupDate(dateStr) {
  if (!dateStr || dateStr === '—') return { day: '—', date: '—' };
  const d = new Date(dateStr + 'T00:00:00');
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(2);
  return { day: DAY_NAMES[d.getDay()], date: `${dd}.${mm}.${yy}` };
}

function groupByDateCrop(rows) {
  const map = new Map();
  const order = [];
  rows.forEach(r => {
    const dateKey = r.feed_date ? String(r.feed_date).split('T')[0] : '—';
    const key = `${r.crop_id || ''}_${dateKey}`;
    if (!map.has(key)) { map.set(key, { dateKey, cropCode: r.crop_code || '', rows: [] }); order.push(key); }
    map.get(key).rows.push(r);
  });
  return order.map(k => map.get(k));
}

function aggregatePondRows(dateGroupRows) {
  const pondMap = new Map();
  const order = [];
  dateGroupRows.forEach(r => {
    const key = String(r.pond_no);
    if (!pondMap.has(key)) {
      pondMap.set(key, { pond_no: r.pond_no, doc: r.doc, feed_type: r.feed_type, f1: 0, f2: 0, f3: 0, f4: 0, day_feed: 0, cum_feed: 0, bf1: 0, bf2: 0, bags: 0, bag_weight: parseFloat(r.bag_weight || 0), loose_kg: 0, entries: [] });
      order.push(key);
    }
    const agg = pondMap.get(key);
    agg.f1 += parseFloat(r.f1 || 0);
    agg.f2 += parseFloat(r.f2 || 0);
    agg.f3 += parseFloat(r.f3 || 0);
    agg.f4 += parseFloat(r.f4 || 0);
    agg.day_feed  += parseFloat(r.day_feed  || 0);
    agg.cum_feed  += parseFloat(r.cum_feed  || 0);
    agg.bf1       += parseFloat(r.bf1       || 0);
    agg.bf2       += parseFloat(r.bf2       || 0);
    agg.bags      += parseFloat(r.bags      || 0);
    agg.loose_kg  += parseFloat(r.loose_kg  || 0);
    agg.entries.push(r);
  });
  return order.map(k => pondMap.get(k));
}

function buildPondRows(feedDate, releaseMap, farmPonds, rows) {
  const pondList = farmPonds.map(p => p.pond_name).sort();
  if (pondList.length === 0) return [makeRow()];
  return pondList.map(pondNo => {
    const last = rows.find(r => String(r.pond_no) === pondNo);
    const lastFeedNo = last
      ? (parseFloat(last.f4) > 0 ? 'F4' : parseFloat(last.f3) > 0 ? 'F3' : parseFloat(last.f2) > 0 ? 'F2' : 'F1')
      : 'F1';
    return {
      ...makeRow(),
      pondNo,
      isPrefilled: true,
      doc: calcDocFromRelease(pondNo, feedDate, releaseMap),
      feedName: last?.feed_name || '',
      feedType: last?.feed_type || 'CP-1',
      feedNo: lastFeedNo,
      bagWeight: last?.bag_weight ? String(last.bag_weight) : '25',
    };
  });
}

export default function AquaFeed({ cropId, cropCode, activeCrops = [], role = 'admin' }) {
  const canWrite  = role !== 'readonly';
  const canDelete = role === 'admin';

  const [rows, setRows]               = useState([]);
  const [summary, setSummary]         = useState([]);
  const [stock, setStock]             = useState([]);
  const [productNames, setProductNames] = useState([]);
  const [farmPonds, setFarmPonds]     = useState([]);
  const [releaseMap, setReleaseMap]   = useState({});
  const [seedMap, setSeedMap]         = useState({});
  const [frpMap, setFrpMap]           = useState({});

  const [hdr, setHdr]                 = useState({ ...EMPTY_HDR, cropId: cropId || '' });
  const [formRows, setFormRows]       = useState([makeRow()]);
  const [editId, setEditId]           = useState(null);
  const [formErr, setFormErr]         = useState('');
  const [formSaving, setFormSaving]   = useState(false);

  const [tab, setTab]                 = useState('list');
  const [filterPond, setFilterPond]   = useState('ALL');
  const [filterCrop, setFilterCrop]   = useState(cropId || '');
  const [loading, setLoading]         = useState(true);

  const [cpl, setCpl]               = useState('');
  const [cSurvival, setCSurvival]   = useState('95');
  const [cDoc, setCDoc]             = useState('');
  const [cAbw, setCAbw]             = useState('');
  const [cFeedRate, setCFeedRate]   = useState('3.5');

  const load = () => {
    setLoading(true);
    Promise.all([
      aquaApi.getFeed(filterCrop || null),
      filterCrop ? aquaApi.getFeedSummary(filterCrop) : Promise.resolve([]),
      aquaApi.getOwnerStock(),
    ]).then(([r, s, st]) => { setRows(r); setSummary(s); setStock(st); }).finally(() => setLoading(false));
  };

  useEffect(() => {
    aquaApi.getProductNames().then(d => { if (Array.isArray(d)) setProductNames(d.slice().sort((a, b) => a.name.localeCompare(b.name))); }).catch(() => {});
    aquaApi.getPonds().then(d => { if (Array.isArray(d)) setFarmPonds(d.slice().sort((a, b) => a.pond_name.localeCompare(b.pond_name))); }).catch(() => {});
  }, []);

  useEffect(() => {
    const cid = hdr.cropId;
    if (!cid) { setReleaseMap({}); setSeedMap({}); setFrpMap({}); return; }
    aquaApi.getCropRelease(cid).then(data => {
      if (Array.isArray(data)) {
        const rmap = {}, smap = {};
        data.forEach(r => {
          rmap[r.pond_no] = r.release_date;
          if (r.seed_count != null) smap[r.pond_no] = r.seed_count;
        });
        setReleaseMap(rmap);
        setSeedMap(smap);
      }
    }).catch(() => { setReleaseMap({}); setSeedMap({}); });
    aquaApi.getFeedFrp(cid).then(data => {
      if (Array.isArray(data)) {
        const map = {};
        data.forEach(r => { map[r.feed_name] = parseFloat(r.frp || 0); });
        setFrpMap(map);
      }
    }).catch(() => setFrpMap({}));
  }, [hdr.cropId]);
  useEffect(() => { load(); }, [filterCrop]);
  useEffect(() => { setFilterCrop(cropId || ''); }, [cropId]);
  useEffect(() => {
    setHdr(h => ({ ...h, cropId: cropId || '' }));
  }, [cropId]);

  const setH = (k, v) => setHdr(h => ({ ...h, [k]: v }));

  const updateRow = (idx, k, v) => {
    setFormRows(prev => {
      const next = prev.map((r, i) => i !== idx ? r : { ...r, [k]: v });
      const row = next[idx];
      if (k === 'amount' || k === 'bagWeight') {
        const amt   = parseFloat(k === 'amount'    ? v : row.amount)    || 0;
        const bw    = parseFloat(k === 'bagWeight' ? v : row.bagWeight) || 0;
        const bags  = bw > 0 ? Math.floor(amt / bw) : 0;
        const loose = bw > 0 ? parseFloat((amt - bags * bw).toFixed(3)) : amt;
        const frp   = frpMap[row.feedName] || 0;
        next[idx] = { ...next[idx], bags: String(bags), looseKg: String(loose), cumFeed: amt.toFixed(3), feedCost: (amt * frp).toFixed(2) };
      }
      if (k === 'feedName') {
        const amt  = parseFloat(row.amount) || 0;
        const frp  = frpMap[v] || 0;
        next[idx] = { ...next[idx], feedCost: (amt * frp).toFixed(2) };
      }
      if (k === 'pondNo') {
        const computed = calcDocFromRelease(v, hdr.feedDate, releaseMap);
        next[idx] = { ...next[idx], doc: computed || '' };
      }
      return next;
    });
  };

  // Recalculate DOC for all rows when releaseMap loads, feed date changes, or form tab opens
  useEffect(() => {
    if (tab !== 'form') return;
    setFormRows(prev => prev.map(row => {
      if (!row.pondNo) return row;
      const computed = calcDocFromRelease(row.pondNo, hdr.feedDate, releaseMap);
      return { ...row, doc: computed || row.doc };
    }));
  }, [releaseMap, hdr.feedDate, tab]);

  const addRow    = () => setFormRows(prev => [...prev, makeRow()]);
  const removeRow = (idx) => setFormRows(prev => prev.filter((_, i) => i !== idx));

  const validRows = formRows.filter(r => r.pondNo && r.feedName && dayTotal(r) > 0);
  const isFormValid = !!(hdr.cropId && hdr.feedDate && (editId ? formRows.length > 0 && formRows[0].pondNo && formRows[0].feedName : validRows.length > 0));

  const handleSave = async () => {
    if (!hdr.cropId)   { setFormErr('Select a crop'); return; }
    if (!hdr.feedDate) { setFormErr('Select a date'); return; }
    const toSave = editId ? formRows : validRows;
    if (toSave.length === 0) { setFormErr('Add at least one row with Pond, Feed Name, and quantity > 0'); return; }
    setFormSaving(true); setFormErr('');
    try {
      // Stock check — only for new entries (edits modify existing usage)
      if (!editId) {
        const stockData = await aquaApi.getOwnerStock();
        const stockMap = {};
        if (Array.isArray(stockData)) {
          stockData.forEach(s => { stockMap[s.product_name] = parseFloat(s.balance_qty || 0); });
        }
        // Aggregate total amount needed per product across all rows being saved
        const needed = {};
        toSave.forEach(row => {
          const amt = parseFloat(row.amount) || 0;
          if (amt > 0 && row.feedName) needed[row.feedName] = (needed[row.feedName] || 0) + amt;
        });
        const errors = [];
        for (const [name, qty] of Object.entries(needed)) {
          if (!(name in stockMap)) continue; // product not in stock purchases → allow
          const avail = stockMap[name];
          if (qty > avail) {
            errors.push(`• ${name}: trying to use ${qty.toFixed(3)} kg but only ${avail.toFixed(3)} kg available (would go negative by ${(qty - avail).toFixed(3)} kg)`);
          }
        }
        if (errors.length > 0) {
          setFormErr('🚫 Stock would go negative — correct the quantities before saving:\n' + errors.join('\n'));
          return;
        }
      }

      for (const row of toSave) {
        const amt = parseFloat(row.amount) || 0;
        const fn  = row.feedNo || 'F1';
        const f1 = fn === 'F1' ? amt : 0;
        const f2 = fn === 'F2' ? amt : 0;
        const f3 = fn === 'F3' ? amt : 0;
        const f4 = fn === 'F4' ? amt : 0;
        const payload = {
          cropId: hdr.cropId, feedDate: hdr.feedDate,
          doc: row.doc || calcDocFromRelease(row.pondNo, hdr.feedDate, releaseMap),
          pondNo: row.pondNo, feedName: row.feedName, feedType: row.feedType,
          f1, f2, f3, f4,
          bf1: parseFloat(row.bf1) || 0, bf2: parseFloat(row.bf2) || 0,
          bags: row.bags, bagWeight: row.bagWeight, looseKg: row.looseKg,
          totalKg: amt,
          cumFeed: row.cumFeed,
          feedTime: row.feedTime,
          feedCost: parseFloat(row.feedCost) || 0,
        };
        if (editId) await aquaApi.updateFeed(editId, payload);
        else        await aquaApi.createFeed(payload);
      }
      setHdr({ ...EMPTY_HDR, cropId: filterCrop || cropId || '' });
      setFormRows([makeRow()]); setEditId(null); setFormErr('');
      setTab('list'); load();
    } catch (e) { setFormErr(e.message); }
    finally { setFormSaving(false); }
  };

  const handleEdit = (r) => {
    setHdr({ cropId: String(r.crop_id), feedDate: r.feed_date ? r.feed_date.split('T')[0] : '' });
    const feedNo = parseFloat(r.f4) > 0 ? 'F4'
                 : parseFloat(r.f3) > 0 ? 'F3'
                 : parseFloat(r.f2) > 0 ? 'F2'
                 : 'F1';
    const amount = String(parseFloat(r[feedNo.toLowerCase()]) || parseFloat(r.day_feed) || '0');
    setFormRows([{
      pondNo:   r.pond_no    || '',
      feedName: r.feed_name  || '',
      feedType: r.feed_type  || 'CP-1',
      feedNo, amount,
      doc:       String(r.doc        || ''),
      bf1:       String(r.bf1        || ''),
      bf2:       String(r.bf2        || ''),
      bags:      String(r.bags       || '0'),
      bagWeight: String(r.bag_weight || '25'),
      looseKg:   String(r.loose_kg   || '0'),
      cumFeed:   String(r.cum_feed   || '0'),
      feedTime:  r.feed_time || '',
      feedCost:  String(r.feed_cost  || '0'),
    }]);
    setEditId(r.id); setFormErr(''); setTab('form');
  };

  const showCropCol  = !filterCrop;
  const displayed    = rows.filter(r => filterPond === 'ALL' || r.pond_no === filterPond);
  const totalFeedAll = rows.reduce((s, r) => s + parseFloat(r.day_feed || 0), 0);
  const pondOptions  = farmPonds.length > 0 ? farmPonds.map(p => p.pond_name) : [];

  const cellSt = { padding: '3px 5px', verticalAlign: 'middle' };
  const inSt   = { padding: '3px 6px', fontSize: 12, height: 28, boxSizing: 'border-box', width: '100%' };
  const roSt   = { ...inSt, background: 'rgba(19,115,51,.10)', color: '#137333', fontWeight: 700, cursor: 'default' };

  if (loading) return <div className="loading-msg">Loading feed log…</div>;

  return (
    <div>
      <div className="tabs">
        <button className={`tab${tab === 'list'   ? ' active' : ''}`} onClick={() => { setTab('list'); setEditId(null); }}>📋 Feed Log</button>
        {canWrite && (
          <button className={`tab${tab === 'form' ? ' active' : ''}`}
            onClick={() => {
              if (!editId) {
                const newCropId = filterCrop || cropId || '';
                setHdr({ ...EMPTY_HDR, cropId: newCropId });
                setFormErr('');
                setFormRows(newCropId ? buildPondRows(today, releaseMap, farmPonds, rows) : [makeRow()]);
              }
              setTab('form');
            }}>
            {editId ? '✏️ Edit Entry' : '➕ Add Entry'}
          </button>
        )}
        <button className={`tab${tab === 'report'  ? ' active' : ''}`} onClick={() => setTab('report')}>📊 Summary</button>
        <button className={`tab${tab === 'stock'   ? ' active' : ''}`} onClick={() => setTab('stock')}>📦 Stock</button>
        <button className={`tab${tab === 'compute' ? ' active' : ''}`} onClick={() => setTab('compute')}>🧮 Feed Chart</button>
      </div>

      {/* Crop filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '8px 12px',
        background: '#f8f9fa', borderRadius: 8, border: '1px solid #e8eaed' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#5f6368' }}>🦐 Crop:</span>
        <select className="form-control" style={{ width: 200, padding: '4px 8px' }}
          value={filterCrop}
          onChange={e => { setFilterCrop(e.target.value); setHdr(h => ({ ...h, cropId: e.target.value })); }}>
          <option value="">All Active Crops</option>
          {activeCrops.slice().sort((a, b) => a.crop_code.localeCompare(b.crop_code)).map(c => <option key={c.id} value={c.id}>{c.crop_code}</option>)}
        </select>
        {!filterCrop && <span style={{ fontSize: 12, color: '#1a73e8', fontWeight: 600 }}>Showing all crops</span>}
      </div>

      {/* ── Feed Log list ───────────────────────────────────────────────────────── */}
      {tab === 'list' && (
        <div>
          <div className="stats-grid" style={{ marginBottom: 16 }}>
            <div className="stat-card">
              <div className="stat-icon green" style={{ fontSize: 22 }}>🐟</div>
              <div><div className="stat-label">Total Feed (Kg)</div><div className="stat-value">{fmt3(totalFeedAll)}</div></div>
            </div>
            <div className="stat-card">
              <div className="stat-icon blue" style={{ fontSize: 22 }}>📋</div>
              <div><div className="stat-label">Log Entries</div><div className="stat-value">{rows.length}</div></div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">🐟 Feed Log {filterCrop && cropCode ? `— ${cropCode}` : ''}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <label style={{ fontSize: 13 }}>Pond:</label>
                <select className="form-control" style={{ width: 120, padding: '4px 8px' }}
                  value={filterPond} onChange={e => setFilterPond(e.target.value)}>
                  <option value="ALL">All</option>
                  {pondOptions.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
              {(() => {
                const groups      = groupByDateCrop(displayed);
                const allPondRows = groups.flatMap(g => aggregatePondRows(g.rows));
                const grandF1    = allPondRows.reduce((s, p) => s + p.f1,       0);
                const grandF2    = allPondRows.reduce((s, p) => s + p.f2,       0);
                const grandF3    = allPondRows.reduce((s, p) => s + p.f3,       0);
                const grandF4    = allPondRows.reduce((s, p) => s + p.f4,       0);
                const grandDay   = allPondRows.reduce((s, p) => s + p.day_feed, 0);
                const grandCumF  = allPondRows.reduce((s, p) => s + p.cum_feed, 0);
                const grandBagWt = allPondRows[0]?.bag_weight || 0;
                const { bags: grandBags, loose: grandLoose } = computeBagsLoose(grandDay, grandBagWt);
                return (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ minWidth: 76, textAlign: 'center' }}>Date / DOC</th>
                        <th style={{ textAlign: 'right' }}>SEED</th>
                        <th style={{ textAlign: 'right' }}>BF1</th>
                        <th style={{ textAlign: 'right' }}>BF2</th>
                        <th>Pond</th>
                        <th style={{ textAlign: 'right' }}>F1</th>
                        <th style={{ textAlign: 'right' }}>F2</th>
                        <th style={{ textAlign: 'right' }}>F3</th>
                        <th style={{ textAlign: 'right' }}>F4</th>
                        <th style={{ textAlign: 'right' }}>Day Feed</th>
                        <th style={{ textAlign: 'right' }}>CUMF</th>
                        <th>Type</th>
                        <th style={{ textAlign: 'right' }}>BAG WT</th>
                        <th style={{ textAlign: 'right' }}>BAGs</th>
                        <th style={{ textAlign: 'right' }}>LOOSE</th>
                        <th style={{ textAlign: 'right' }}>TOT CUM (KG)</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayed.length === 0 && (
                        <tr><td colSpan={17} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No feed entries</td></tr>
                      )}
                      {groups.flatMap((grp, gi) => {
                        const { day, date } = fmtGroupDate(grp.dateKey);
                        const docVal   = grp.rows[0]?.doc;
                        const pondRows = aggregatePondRows(grp.rows);
                        const gF1  = pondRows.reduce((s, p) => s + p.f1,       0);
                        const gF2  = pondRows.reduce((s, p) => s + p.f2,       0);
                        const gF3  = pondRows.reduce((s, p) => s + p.f3,       0);
                        const gF4  = pondRows.reduce((s, p) => s + p.f4,       0);
                        const gDay = pondRows.reduce((s, p) => s + p.day_feed, 0);
                        const dataRows = pondRows.map((pr, ri) => {
                          const { bags: cBags, loose: cLoose } = computeBagsLoose(pr.day_feed, pr.bag_weight);
                          return (
                          <tr key={`r_${gi}_${ri}`}>
                            {ri === 0 && (
                              <td rowSpan={pondRows.length + 1} style={{
                                padding: 0, textAlign: 'center', verticalAlign: 'middle',
                                whiteSpace: 'nowrap', borderRight: '3px solid #3d3b8e',
                                background: '#fff', minWidth: 76,
                              }}>
                                {showCropCol && grp.cropCode && (
                                  <div style={{ fontSize: 9, padding: '2px 4px', color: '#7b6ea0', background: '#f3f0fa', borderBottom: '1px solid #e8e0f5' }}>{grp.cropCode}</div>
                                )}
                                <div style={{ padding: '10px 6px 7px', fontWeight: 800, fontSize: 22, color: '#1a237e', letterSpacing: 2, borderBottom: '1px solid #d0d0ee' }}>{day}</div>
                                <div style={{ background: '#3d3b8e', color: '#fff', padding: '6px 6px', fontWeight: 700, fontSize: 13, letterSpacing: 0.5 }}>{date}</div>
                                {docVal && <div style={{ padding: '8px 6px 10px', fontWeight: 900, fontSize: 42, color: '#1a237e', lineHeight: 1 }}>{docVal}</div>}
                              </td>
                            )}
                            <td style={{ textAlign: 'right', fontSize: 12 }}>{seedMap[pr.pond_no] != null ? Number(seedMap[pr.pond_no]).toLocaleString() : '—'}</td>
                            <td style={{ textAlign: 'right', fontSize: 12 }}>{pr.bf1 > 0 ? f3d(pr.bf1) : '—'}</td>
                            <td style={{ textAlign: 'right', fontSize: 12 }}>{pr.bf2 > 0 ? f3d(pr.bf2) : '—'}</td>
                            <td><span style={{ background: '#eaeaf5', color: '#1a237e', borderRadius: 8, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>{pr.pond_no}</span></td>
                            <td style={{ textAlign: 'right' }}>{f3d(pr.f1)}</td>
                            <td style={{ textAlign: 'right' }}>{f3d(pr.f2)}</td>
                            <td style={{ textAlign: 'right' }}>{f3d(pr.f3)}</td>
                            <td style={{ textAlign: 'right' }}>{f3d(pr.f4)}</td>
                            <td style={{ textAlign: 'right', fontWeight: 700, color: '#1a237e' }}>{f3d(pr.day_feed)}</td>
                            <td style={{ textAlign: 'right' }}>{f3d(pr.cum_feed)}</td>
                            <td>{pr.feed_type}</td>
                            <td style={{ textAlign: 'right' }}>{pr.bag_weight > 0 ? pr.bag_weight : '—'}</td>
                            <td style={{ textAlign: 'right' }}>{cBags >= 1 ? cBags : '—'}</td>
                            <td style={{ textAlign: 'right' }}>{f3d(cLoose)}</td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>{f3d(pr.day_feed)}</td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              {canWrite && <button className="btn-sm btn-primary" onClick={() => handleEdit(pr.entries[pr.entries.length - 1])}>✏️ Edit</button>}
                            </td>
                          </tr>
                          );
                        });
                        const gCumF  = pondRows.reduce((s, p) => s + p.cum_feed, 0);
                        const gBagWt = pondRows[0]?.bag_weight || 0;
                        const { bags: gBags, loose: gLoose } = computeBagsLoose(gDay, gBagWt);
                        const totalRow = (
                          <tr key={`t_${gi}`} style={{ background: '#8585b5', fontWeight: 700 }}>
                            <td colSpan={4} style={{ textAlign: 'right', padding: '5px 8px', color: '#fff', fontSize: 12 }}>TOTAL</td>
                            <td style={{ textAlign: 'right', color: '#fff' }}>{f3d(gF1)}</td>
                            <td style={{ textAlign: 'right', color: '#fff' }}>{f3d(gF2)}</td>
                            <td style={{ textAlign: 'right', color: '#fff' }}>{f3d(gF3)}</td>
                            <td style={{ textAlign: 'right', color: '#fff' }}>{f3d(gF4)}</td>
                            <td style={{ textAlign: 'right', color: '#fff' }}>{f3d(gDay)}</td>
                            <td style={{ textAlign: 'right', color: '#fff' }}>{f3d(gCumF)}</td>
                            <td></td>
                            <td></td>
                            <td style={{ textAlign: 'right', color: '#fff' }}>{gBags > 0 ? Math.round(gBags) : '—'}</td>
                            <td style={{ textAlign: 'right', color: '#fff' }}>{f3d(gLoose)}</td>
                            <td></td>
                            <td></td>
                          </tr>
                        );
                        return [...dataRows, totalRow];
                      })}
                    </tbody>
                    {displayed.length > 0 && (
                      <tfoot>
                        <tr>
                          <td colSpan={5} style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>GRAND TOTAL</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>{f3d(grandF1)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>{f3d(grandF2)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>{f3d(grandF3)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>{f3d(grandF4)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px', color: '#1a237e' }}>{f3d(grandDay)} Kg</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>{f3d(grandCumF)}</td>
                          <td></td>
                          <td></td>
                          <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>{grandBags > 0 ? Math.round(grandBags) : '—'}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>{f3d(grandLoose)}</td>
                          <td></td>
                          <td></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit form ─────────────────────────────────────────────────────── */}
      {tab === 'form' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">{editId ? '✏️ Edit Feed Entry' : '➕ Add Feed Entry'}</div>
          </div>
          <div className="card-body">
            {formErr && <div className="alert alert-error" style={{ marginBottom: 12, whiteSpace: 'pre-line' }}>{formErr}</div>}

            {/* Header row: Crop | Date */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 12 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Crop <span style={{ color: '#c5221f' }}>*</span></label>
                <select className="form-control" value={hdr.cropId}
                  onChange={e => {
                    setH('cropId', e.target.value);
                    if (!editId) setFormRows(e.target.value ? buildPondRows(hdr.feedDate, releaseMap, farmPonds, rows) : [makeRow()]);
                  }}
                  disabled={!!editId}>
                  <option value="">— Select Crop —</option>
                  {activeCrops.slice().sort((a, b) => a.crop_code.localeCompare(b.crop_code)).map(c => <option key={c.id} value={c.id}>{c.crop_code}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Date <span style={{ color: '#c5221f' }}>*</span></label>
                <DateInput className="form-control" value={hdr.feedDate} onChange={e => setH('feedDate', e.target.value)} />
              </div>
            </div>
            {hdr.cropId && Object.keys(releaseMap).length === 0 && (
              <div style={{ marginBottom: 12, padding: '7px 12px', background: '#fff8e1', borderRadius: 8,
                border: '1px solid #ffe082', fontSize: 12, color: '#e37400' }}>
                ⚠️ No pond release dates set for this crop — DOC will be blank. Add release dates in Crop Management → 🚀 Release Dates.
              </div>
            )}
            {hdr.cropId && Object.keys(releaseMap).length > 0 && (
              <div style={{ marginBottom: 12, padding: '7px 12px', background: '#e8f0fe', borderRadius: 8,
                border: '1px solid #c5d9f5', fontSize: 12, color: '#1a73e8' }}>
                📅 DOC auto-calculated per pond from its release date. Select a pond in a row to see its DOC.
              </div>
            )}

            {farmPonds.length === 0 && (
              <div style={{ marginBottom: 12, padding: '8px 14px', background: '#fff8e1', borderRadius: 8,
                border: '1px solid #ffe082', fontSize: 13, color: '#e37400' }}>
                ⚠️ No ponds defined — add farm ponds in Crop Management → 🏊 Ponds tab first.
              </div>
            )}

            {/* Line items table */}
            <div style={{ marginBottom: 8, padding: '7px 12px', background: '#e8f0fe', borderRadius: 8,
              border: '1px solid #c5d9f5', fontSize: 12, color: '#1a73e8' }}>
              💡 Enter <strong>Weight (Kg)</strong> — DOC, Bags, Loose, Cum &amp; Cost auto-fill. All fields are editable to override.
            </div>
            <div style={{ marginBottom: 6, fontWeight: 700, fontSize: 13, color: '#3c4043' }}>🐟 Feed Rows</div>
            <div style={{ overflowX: 'auto', marginBottom: 0 }}>
              <table style={{ borderCollapse: 'collapse', minWidth: 1020, width: '100%', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f1f3f4' }}>
                    <th style={{ ...cellSt, width: 28, textAlign: 'center' }}>#</th>
                    <th style={{ ...cellSt, width: 68, textAlign: 'right' }}>BF1</th>
                    <th style={{ ...cellSt, width: 68, textAlign: 'right' }}>BF2</th>
                    <th style={{ ...cellSt, width: 110 }}>Pond <span style={{ color: '#c5221f' }}>*</span></th>
                    <th style={{ ...cellSt, width: 52, textAlign: 'center' }}>DOC</th>
                    <th style={{ ...cellSt, minWidth: 150 }}>Feed Name <span style={{ color: '#c5221f' }}>*</span></th>
                    <th style={{ ...cellSt, width: 72, textAlign: 'center' }}>Feed No</th>
                    <th style={{ ...cellSt, width: 88, textAlign: 'right' }}>Weight (Kg)</th>
                    <th style={{ ...cellSt, width: 76, textAlign: 'right' }}>Day (Kg)</th>
                    <th style={{ ...cellSt, width: 60, textAlign: 'right' }}>Bags</th>
                    <th style={{ ...cellSt, width: 68, textAlign: 'right' }}>Bag Wt</th>
                    <th style={{ ...cellSt, width: 68, textAlign: 'right' }}>Loose (Kg)</th>
                    <th style={{ ...cellSt, width: 80, textAlign: 'right' }}>Cum (Kg)</th>
                    <th style={{ ...cellSt, width: 76 }}>Time</th>
                    <th style={{ ...cellSt, width: 28 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {formRows.map((row, idx) => {
                    const dt = dayTotal(row);
                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid #e8eaed' }}>
                        <td style={{ ...cellSt, textAlign: 'center', color: '#888', fontWeight: 700 }}>{idx + 1}</td>
                        <td style={cellSt}>
                          <input className="form-control" style={{ ...inSt, textAlign: 'right' }}
                            type="number" step="0.01" min="0" placeholder="—" value={row.bf1}
                            onChange={e => updateRow(idx, 'bf1', e.target.value)} />
                        </td>
                        <td style={cellSt}>
                          <input className="form-control" style={{ ...inSt, textAlign: 'right' }}
                            type="number" step="0.01" min="0" placeholder="—" value={row.bf2}
                            onChange={e => updateRow(idx, 'bf2', e.target.value)} />
                        </td>
                        <td style={cellSt}>
                          {row.isPrefilled ? (
                            <span style={{
                              display: 'inline-block', padding: '3px 10px', borderRadius: 8,
                              background: '#e6f4ea', color: '#137333', fontWeight: 700, fontSize: 12,
                            }}>{row.pondNo}</span>
                          ) : (
                            <select className="form-control" style={inSt}
                              value={row.pondNo} onChange={e => updateRow(idx, 'pondNo', e.target.value)}>
                              <option value="">— Pond —</option>
                              {pondOptions.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                          )}
                        </td>
                        <td style={{ ...cellSt, textAlign: 'center' }}>
                          <input className="form-control" style={{ ...inSt, textAlign: 'center',
                            background: row.doc ? 'rgba(26,115,232,.10)' : undefined,
                            color: row.doc ? '#1a73e8' : undefined, fontWeight: row.doc ? 700 : undefined }}
                            type="number" min="1" step="1" placeholder="—"
                            value={row.doc}
                            onChange={e => updateRow(idx, 'doc', e.target.value)} />
                        </td>
                        <td style={cellSt}>
                          <select className="form-control" style={{ ...inSt}}
                            value={row.feedName} onChange={e => updateRow(idx, 'feedName', e.target.value)}>
                            <option value="">— Feed Name —</option>
                            {productNames.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                          </select>
                        </td>
                        <td style={{ ...cellSt, textAlign: 'center' }}>
                          <select className="form-control" style={{ ...inSt, textAlign: 'center', fontWeight: 700 }}
                            value={row.feedNo} onChange={e => updateRow(idx, 'feedNo', e.target.value)}>
                            {FEED_NOS.map(f => <option key={f} value={f}>{f}</option>)}
                          </select>
                        </td>
                        <td style={cellSt}>
                          <input className="form-control" style={{ ...inSt, textAlign: 'right' }}
                            type="number" step="0.001" min="0" value={row.amount}
                            onChange={e => updateRow(idx, 'amount', e.target.value)} />
                        </td>
                        <td style={cellSt}>
                          <input className="form-control" style={{ ...roSt, textAlign: 'right' }}
                            readOnly value={dt > 0 ? dt.toFixed(3) : ''} placeholder="0.000" />
                        </td>
                        <td style={cellSt}>
                          <input className="form-control" style={{ ...inSt, textAlign: 'right' }}
                            type="number" step="1" min="0" value={row.bags}
                            onChange={e => updateRow(idx, 'bags', e.target.value)} />
                        </td>
                        <td style={cellSt}>
                          <input className="form-control" style={{ ...inSt, textAlign: 'right' }}
                            type="number" step="0.01" min="0" value={row.bagWeight}
                            onChange={e => updateRow(idx, 'bagWeight', e.target.value)} />
                        </td>
                        <td style={cellSt}>
                          <input className="form-control" style={{ ...inSt, textAlign: 'right' }}
                            type="number" step="0.001" min="0" value={row.looseKg}
                            onChange={e => updateRow(idx, 'looseKg', e.target.value)} />
                        </td>
                        <td style={cellSt}>
                          <input className="form-control" style={{ ...inSt, textAlign: 'right' }}
                            type="number" step="0.001" min="0" value={row.cumFeed}
                            onChange={e => updateRow(idx, 'cumFeed', e.target.value)} />
                        </td>
                        <td style={cellSt}>
                          <input className="form-control" style={inSt}
                            type="time" value={row.feedTime}
                            onChange={e => updateRow(idx, 'feedTime', e.target.value)} />
                        </td>
                        <td style={{ ...cellSt, textAlign: 'center' }}>
                          {formRows.length > 1 && (
                            <button onClick={() => removeRow(idx)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer',
                                color: '#c5221f', fontWeight: 900, fontSize: 16, lineHeight: 1, padding: '0 4px' }}
                              title="Remove row">×</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Add row + totals footer */}
            {!editId && (
              <div style={{ marginTop: 8, marginBottom: 14 }}>
                <button onClick={addRow}
                  style={{ padding: '5px 16px', borderRadius: 6, border: '1px dashed #1a73e8',
                    background: '#e8f0fe', color: '#1a73e8', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
                  + Add Row
                </button>
                {validRows.length > 0 && (
                  <span style={{ marginLeft: 14, fontSize: 13, color: '#137333', fontWeight: 600 }}>
                    Total: {fmt3(validRows.reduce((s, r) => s + dayTotal(r), 0))} Kg across {validRows.length} pond{validRows.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button className="btn btn-primary" onClick={handleSave}
                disabled={formSaving || !isFormValid}
                style={{ opacity: isFormValid ? 1 : 0.45, cursor: isFormValid ? 'pointer' : 'not-allowed' }}>
                {formSaving ? 'Saving…' : editId ? '💾 Update' : `➕ Save ${validRows.length > 0 ? validRows.length + ' entr' + (validRows.length === 1 ? 'y' : 'ies') : 'Entries'}`}
              </button>
              <button className="btn btn-secondary" onClick={() => { setTab('list'); setEditId(null); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Summary ─────────────────────────────────────────────────────────────── */}
      {tab === 'report' && (
        <div className="card">
          <div className="card-header"><div className="card-title">📊 Feed Summary by Pond {filterCrop && cropCode ? `— ${cropCode}` : ''}</div></div>
          <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Pond</th>
                  <th style={{ textAlign: 'right' }}>Days Fed</th>
                  <th style={{ textAlign: 'right' }}>Total Feed (Kg)</th>
                  <th style={{ textAlign: 'right' }}>Cumulative (Kg)</th>
                </tr>
              </thead>
              <tbody>
                {summary.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: '#888', padding: 24 }}>Select a crop to see summary</td></tr>}
                {summary.map(r => (
                  <tr key={r.pond_no}>
                    <td><span style={{ background: '#e6f4ea', color: '#137333', borderRadius: 8, padding: '2px 10px', fontWeight: 600 }}>{r.pond_no}</span></td>
                    <td style={{ textAlign: 'right' }}>{r.days}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt3(r.total_feed)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt3(r.cum_feed)}</td>
                  </tr>
                ))}
              </tbody>
              {summary.length > 0 && (
                <tfoot>
                  <tr>
                    <td style={{ fontWeight: 700, padding: '10px 12px' }}>TOTAL</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>{summary.reduce((s, r) => s + parseInt(r.days || 0), 0)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px', color: '#137333' }}>{fmt3(summary.reduce((s, r) => s + parseFloat(r.total_feed || 0), 0))} Kg</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* ── Owner Stock Balance ──────────────────────────────────────────────────── */}
      {tab === 'stock' && (
        <div>
          {stock.length === 0 ? (
            <div className="card">
              <div className="card-body" style={{ textAlign: 'center', color: '#888', padding: 48 }}>
                No stock data. Add purchases under Purchases → Stock Purchases to track inventory.
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-header"><div className="card-title">📦 Stock Balance (Purchases − Feed Used − Chem Used)</div></div>
              <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Product Name</th><th>Category</th><th>UOM</th>
                      <th style={{ textAlign: 'right' }}>Purchased</th>
                      <th style={{ textAlign: 'right' }}>Chem Used</th>
                      <th style={{ textAlign: 'right' }}>Feed Used</th>
                      <th style={{ textAlign: 'right' }}>Balance</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stock.map((r, i) => {
                      const bal   = parseFloat(r.balance_qty || 0);
                      const isLow = bal > 0 && bal < 50;
                      return (
                        <tr key={i}>
                          <td style={{ fontWeight: 600 }}>{r.product_name}</td>
                          <td><span style={{ background: '#e8f0fe', color: '#1a73e8', borderRadius: 8, padding: '2px 8px', fontSize: 12 }}>{r.category || '—'}</span></td>
                          <td>{r.uom}</td>
                          <td style={{ textAlign: 'right' }}>{parseFloat(r.purchased_qty || 0).toFixed(2)}</td>
                          <td style={{ textAlign: 'right', color: '#7b1fa2' }}>{parseFloat(r.chem_used || 0).toFixed(2)}</td>
                          <td style={{ textAlign: 'right', color: '#e37400' }}>{parseFloat(r.feed_used || 0).toFixed(2)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 800, fontSize: 15,
                            color: bal <= 0 ? '#c5221f' : isLow ? '#e37400' : '#137333' }}>
                            {bal.toFixed(2)}
                          </td>
                          <td>
                            {bal <= 0
                              ? <span style={{ background: '#fce8e6', color: '#c5221f', borderRadius: 8, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>OUT OF STOCK</span>
                              : isLow
                              ? <span style={{ background: '#fef3cd', color: '#e37400', borderRadius: 8, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>LOW STOCK</span>
                              : <span style={{ background: '#e6f4ea', color: '#137333', borderRadius: 8, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>IN STOCK</span>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Feed Compute Chart ──────────────────────────────────────────────────── */}
      {tab === 'compute' && (() => {
        const plNum    = parseFloat(cpl)       || 0;
        const survPct  = parseFloat(cSurvival) || 95;
        const docNum   = parseInt(cDoc)        || 0;
        const abwNum   = parseFloat(cAbw)      || 0;
        const frNum    = parseFloat(cFeedRate) || 0;
        const chartVal = docNum > 0 ? blindChartValue(docNum) : 0;
        const blindFeed = plNum > 0 && docNum > 0 ? (plNum / 100000) * chartVal * (survPct / 100) : 0;
        const population = plNum * (survPct / 100);
        const biomass    = abwNum > 0 ? population * abwNum / 1000 : 0;
        const bioFeed    = biomass > 0 && frNum > 0 ? biomass * frNum / 100 : 0;
        const recFeed    = bioFeed > 0 ? bioFeed : blindFeed;

        const svgW = 580, svgH = 230, padL = 52, padR = 20, padT = 16, padB = 42;
        const cW = svgW - padL - padR, cH = svgH - padT - padB;
        const xp = d => padL + ((d - 1) / 29) * cW;
        const yp = f => padT + (1 - f / 40) * cH;
        const linePath = BLIND_CHART.map(([d, f], i) => `${i === 0 ? 'M' : 'L'} ${xp(d).toFixed(1)} ${yp(f).toFixed(1)}`).join(' ');
        const areaPath = `${linePath} L ${xp(30).toFixed(1)} ${yp(0).toFixed(1)} L ${xp(1).toFixed(1)} ${yp(0).toFixed(1)} Z`;

        const inS = { padding: '5px 8px', fontSize: 13, height: 32, boxSizing: 'border-box', width: '100%' };
        const lS  = { fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#5f6368' };

        return (
          <div>
            {/* Inputs + Results */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              {/* Inputs */}
              <div className="card">
                <div className="card-header"><div className="card-title">🐟 Pond Parameters</div></div>
                <div className="card-body">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={lS}>PL Stocked</label>
                      <input className="form-control" style={inS} type="number" min="0" step="1000"
                        value={cpl} onChange={e => setCpl(e.target.value)} placeholder="e.g. 300000" />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={lS}>Survival %</label>
                      <input className="form-control" style={inS} type="number" min="1" max="100" step="1"
                        value={cSurvival} onChange={e => setCSurvival(e.target.value)} placeholder="95" />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={lS}>DOC</label>
                      <input className="form-control" style={inS} type="number" min="1" max="120" step="1"
                        value={cDoc} onChange={e => setCDoc(e.target.value)} placeholder="e.g. 15" />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={lS}>Avg Body Wt (g)</label>
                      <input className="form-control" style={inS} type="number" min="0" step="0.1"
                        value={cAbw}
                        onChange={e => { setCAbw(e.target.value); const s = suggestedFeedRate(e.target.value); if (s) setCFeedRate(s); }}
                        placeholder="e.g. 5.0" />
                    </div>
                    <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                      <label style={lS}>Feed Rate % <span style={{ fontSize: 11, color: '#aaa', fontWeight: 400 }}>(% of biomass — auto-set from ABW)</span></label>
                      <input className="form-control" style={inS} type="number" min="0" step="0.1"
                        value={cFeedRate} onChange={e => setCFeedRate(e.target.value)} placeholder="3.5" />
                    </div>
                  </div>
                  {/* Reference rates */}
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6, color: '#3c4043' }}>Typical Feed Rates by ABW</div>
                    <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                      <thead><tr style={{ background: '#e8f0fe' }}>
                        <th style={{ padding: '4px 8px', textAlign: 'left', color: '#1a73e8' }}>ABW (g)</th>
                        <th style={{ padding: '4px 8px', textAlign: 'right', color: '#1a73e8' }}>Feed %</th>
                      </tr></thead>
                      <tbody>
                        {ABW_RATES.map((r, i) => (
                          <tr key={i} style={{ background: i % 2 === 0 ? '#fafafa' : '#fff', borderBottom: '1px solid #f1f3f4' }}>
                            <td style={{ padding: '3px 8px', fontWeight: 600 }}>{r.range}</td>
                            <td style={{ padding: '3px 8px', textAlign: 'right', color: '#137333' }}>{r.rate}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="card">
                <div className="card-header"><div className="card-title">📊 Daily Feed Recommendation</div></div>
                <div className="card-body">
                  {!plNum && !cDoc
                    ? <div style={{ textAlign: 'center', color: '#aaa', padding: '32px 0', fontSize: 13 }}>Enter PL Stocked and DOC to see recommendations</div>
                    : <>
                      {blindFeed > 0 && (
                        <div style={{ marginBottom: 12, padding: '10px 14px', background: '#e8f0fe', borderRadius: 8, border: '1px solid #c5d9f5' }}>
                          <div style={{ fontSize: 12, color: '#5f6368', marginBottom: 2 }}>🔵 Blind Feed (DOC {docNum})</div>
                          <div style={{ fontSize: 26, fontWeight: 900, color: '#1a73e8' }}>{blindFeed.toFixed(3)} kg/day</div>
                          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>({(plNum/100000).toFixed(2)} × {chartVal} kg/100K PL × {survPct}% survival)</div>
                        </div>
                      )}
                      {bioFeed > 0 && (
                        <div style={{ marginBottom: 12, padding: '10px 14px', background: '#e6f4ea', borderRadius: 8, border: '1px solid #a8d5b5' }}>
                          <div style={{ fontSize: 12, color: '#5f6368', marginBottom: 2 }}>🟢 Biomass Feed</div>
                          <div style={{ fontSize: 26, fontWeight: 900, color: '#137333' }}>{bioFeed.toFixed(3)} kg/day</div>
                          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Population {Math.round(population).toLocaleString()} × ABW {abwNum}g ÷ 1000 = {biomass.toFixed(1)} kg biomass × {frNum}%</div>
                        </div>
                      )}
                      {recFeed > 0 && (
                        <>
                          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6, color: '#3c4043' }}>⏰ Feed Distribution</div>
                          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                            <thead><tr style={{ background: '#3d3b8e', color: '#fff' }}>
                              <th style={{ padding: '5px 8px', textAlign: 'left' }}>Time</th>
                              <th style={{ padding: '5px 8px', textAlign: 'center' }}>%</th>
                              <th style={{ padding: '5px 8px', textAlign: 'right' }}>Amount (kg)</th>
                            </tr></thead>
                            <tbody>
                              {FEED_DIST.map(fd => (
                                <tr key={fd.time} style={{ borderBottom: '1px solid #f1f3f4' }}>
                                  <td style={{ padding: '4px 8px' }}>{fd.time}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'center', color: '#5f6368' }}>{fd.pct}%</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 700, color: '#137333' }}>{(recFeed * fd.pct / 100).toFixed(3)}</td>
                                </tr>
                              ))}
                              <tr style={{ background: '#8585b5', fontWeight: 700, color: '#fff' }}>
                                <td style={{ padding: '5px 8px' }}>TOTAL</td>
                                <td style={{ padding: '5px 8px', textAlign: 'center' }}>100%</td>
                                <td style={{ padding: '5px 8px', textAlign: 'right' }}>{recFeed.toFixed(3)} kg</td>
                              </tr>
                            </tbody>
                          </table>
                          <div style={{ marginTop: 10, padding: '8px 12px', background: '#fff8e1', borderRadius: 6, border: '1px solid #ffe082', fontSize: 11, color: '#e37400' }}>
                            💡 Tip: Use Blind Feed for DOC 1–25. Switch to Biomass Feed after cast-net sampling confirms ABW & survival.
                          </div>
                        </>
                      )}
                    </>
                  }
                </div>
              </div>
            </div>

            {/* Chart + Table */}
            <div className="card">
              <div className="card-header"><div className="card-title">📈 Blind Feeding Chart — DOC vs Feed (per 100,000 PL stocked)</div></div>
              <div className="card-body">
                {/* SVG Line Chart */}
                <svg width="100%" viewBox={`0 0 ${svgW} ${svgH}`} style={{ display: 'block', maxHeight: 260, marginBottom: 20 }}>
                  <defs>
                    <linearGradient id="feedAreaGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1a73e8" stopOpacity="0.20" />
                      <stop offset="100%" stopColor="#1a73e8" stopOpacity="0.02" />
                    </linearGradient>
                  </defs>
                  {/* Y grid + labels */}
                  {[0, 10, 20, 30, 40].map(v => (
                    <g key={v}>
                      <line x1={padL} y1={yp(v)} x2={padL + cW} y2={yp(v)} stroke="#e8eaed" strokeWidth="1" />
                      <text x={padL - 6} y={yp(v) + 4} textAnchor="end" fontSize="10" fill="#888">{v}</text>
                    </g>
                  ))}
                  {/* X grid + labels */}
                  {[1, 5, 10, 15, 20, 25, 30].map(d => (
                    <g key={d}>
                      <line x1={xp(d)} y1={padT} x2={xp(d)} y2={padT + cH} stroke="#e8eaed" strokeWidth="1" strokeDasharray="3,3" />
                      <text x={xp(d)} y={padT + cH + 16} textAnchor="middle" fontSize="10" fill="#888">{d}</text>
                    </g>
                  ))}
                  {/* Area fill */}
                  <path d={areaPath} fill="url(#feedAreaGrad)" />
                  {/* Line */}
                  <path d={linePath} fill="none" stroke="#1a73e8" strokeWidth="2.5" strokeLinejoin="round" />
                  {/* Data points */}
                  {BLIND_CHART.map(([d, f]) => <circle key={d} cx={xp(d)} cy={yp(f)} r="2.5" fill="#1a73e8" />)}
                  {/* Current DOC marker */}
                  {docNum >= 1 && docNum <= 30 && (
                    <g>
                      <line x1={xp(docNum)} y1={padT} x2={xp(docNum)} y2={padT + cH} stroke="#c5221f" strokeWidth="1.5" strokeDasharray="4,3" />
                      <circle cx={xp(docNum)} cy={yp(chartVal)} r="6" fill="#c5221f" />
                      <rect x={xp(docNum) + 8} y={yp(chartVal) - 20} width={90} height={22} rx={4} fill="#c5221f" fillOpacity="0.9" />
                      <text x={xp(docNum) + 12} y={yp(chartVal) - 4} fontSize="10" fill="#fff" fontWeight="bold">DOC {docNum}: {chartVal} kg</text>
                    </g>
                  )}
                  {/* Axis titles */}
                  <text x={padL + cW / 2} y={svgH - 5} textAnchor="middle" fontSize="11" fill="#5f6368">Day of Culture (DOC)</text>
                  <text x={13} y={padT + cH / 2} textAnchor="middle" fontSize="11" fill="#5f6368" transform={`rotate(-90, 13, ${padT + cH / 2})`}>Feed (kg / 100K PL)</text>
                </svg>

                {/* DOC Table + Formula */}
                <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 16 }}>
                  {/* Scrollable DOC table */}
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6, color: '#3c4043' }}>📋 Chart Reference (DOC 1–30)</div>
                    <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid #e8eaed', borderRadius: 8 }}>
                      <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                        <thead style={{ position: 'sticky', top: 0 }}>
                          <tr style={{ background: '#3d3b8e', color: '#fff' }}>
                            <th style={{ padding: '6px 10px', textAlign: 'center' }}>DOC</th>
                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>kg/100K PL</th>
                            {plNum > 0 && <th style={{ padding: '6px 10px', textAlign: 'right' }}>Your Feed</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {BLIND_CHART.map(([d, f]) => {
                            const isCur = d === docNum;
                            const myFeed = plNum > 0 ? (plNum / 100000) * f * (survPct / 100) : null;
                            return (
                              <tr key={d} style={{ background: isCur ? '#fff3cd' : d % 2 === 0 ? '#fafafa' : '#fff', borderBottom: '1px solid #f1f3f4', fontWeight: isCur ? 700 : 400 }}>
                                <td style={{ padding: '4px 10px', textAlign: 'center', color: isCur ? '#e37400' : '#3c4043' }}>{d}{isCur ? ' ◀' : ''}</td>
                                <td style={{ padding: '4px 10px', textAlign: 'right', color: isCur ? '#e37400' : '#5f6368' }}>{f.toFixed(1)}</td>
                                {plNum > 0 && <td style={{ padding: '4px 10px', textAlign: 'right', fontWeight: 700, color: isCur ? '#c5221f' : '#137333' }}>{myFeed.toFixed(3)}</td>}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Formulas */}
                  <div style={{ fontSize: 13, lineHeight: 1.8, color: '#3c4043' }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>📐 Formulas</div>
                    <div style={{ padding: '12px 16px', background: '#e8f0fe', borderRadius: 8, border: '1px solid #c5d9f5', marginBottom: 12 }}>
                      <div style={{ fontWeight: 700, color: '#1a73e8', marginBottom: 4 }}>🔵 Blind Feed (DOC 1–25)</div>
                      <code style={{ fontSize: 12, color: '#1a237e' }}>Blind Feed = (PL ÷ 100,000) × Chart Value × (Survival% ÷ 100)</code>
                    </div>
                    <div style={{ padding: '12px 16px', background: '#e6f4ea', borderRadius: 8, border: '1px solid #a8d5b5', marginBottom: 12 }}>
                      <div style={{ fontWeight: 700, color: '#137333', marginBottom: 4 }}>🟢 Biomass Feed (DOC 25+)</div>
                      <div style={{ fontSize: 12 }}>Population = PL × Survival%</div>
                      <div style={{ fontSize: 12 }}>Biomass (kg) = Population × ABW (g) ÷ 1000</div>
                      <code style={{ fontSize: 12, color: '#137333' }}>Feed = Biomass × Feed Rate% ÷ 100</code>
                    </div>
                    <div style={{ padding: '12px 16px', background: '#fff8e1', borderRadius: 8, border: '1px solid #ffe082', marginBottom: 12 }}>
                      <div style={{ fontWeight: 700, color: '#e37400', marginBottom: 6 }}>⏰ Feed Distribution (Andhra/Nellore Practice)</div>
                      {FEED_DIST.map(fd => (
                        <div key={fd.time} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '2px 0' }}>
                          <span>{fd.time}</span>
                          <span style={{ fontWeight: 600 }}>{fd.pct}%{recFeed > 0 ? ` — ${(recFeed * fd.pct / 100).toFixed(3)} kg` : ''}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ padding: '10px 14px', background: '#f3e5f5', borderRadius: 8, border: '1px solid #ce93d8', fontSize: 12, color: '#4a148c' }}>
                      <strong>After DOC 25–30:</strong> Gradually shift from blind feeding to biomass-based feeding using weekly cast-net sampling (ABW), survival estimates, feed tray checks, and FCR targets.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
